// Businesses (owner plane, #232) — tenant lifecycle + usage over /api/owner/tenants.
// Owner-only ("Business/Betrieb" is display copy; code says tenant). Lifecycle mutations are
// cross-tenant owner actions and never carry the tenant-context header (the client excludes
// the owner plane). Archive is terminal, so it is guarded by an explicit confirm step.
//
// #378: the prototype's layout — a card list plus a per-business detail page with section
// navigation (General / Capabilities & limits / Mailboxes / Catalog / Access). The former
// Usage/Readiness/Features/Mailboxes/Catalog dialogs became inline sections; API unchanged.
import React from "react";
import { Card, Button, Input, FormField, StatusPill, Banner, Dialog } from "@/ds";
import { Icon, SectionLayout, SectionNav, fmtDate } from "@/lib";
import { useRoutedState } from "@/lib/routed-state";
import type { BusinessSection } from "../router";
import { cockpit } from "@/api";
import { cachedRead } from "@/api/read-cache";
import { useOwnerLiveInvalidation } from "@/live/live-provider";
import type { OwnerTenantReadiness, TenantRow, TenantUsage } from "@/api";
import { useT } from "../i18n";
import {
  NeoMailboxPicker,
  pickedAddress,
  useNeoMailboxOffer,
  type PickerState,
} from "./neo-mailbox-picker";
import { registerBusiness, type MailboxChoice } from "./registration-mailbox.logic";
import { WorkingAddressDialog } from "./working-address";
import { InvitationPanel } from "./invitation-panel";
import { TenantMailboxesEditor } from "./screen-tenant-mailboxes";
import { FeatureDefaultsDialog, TenantFeaturesEditor } from "./screen-tenant-features";
import { TenantCatalogEditor } from "./screen-tenant-catalog";
import { TenantRuleParametersEditor } from "./screen-tenant-rule-parameters";
import type { PushToast } from "@/notifications/toast-queue";

export interface TenantsScreenProps {
  pushToast: PushToast;
  /** The tenant this session currently browses (null = none). */
  tenantContext: string | null;
  /** Set the browsed tenant and jump to its inbox. */
  onBrowse: (tenantId: string) => void;
  /**
   * #1228: which business is open and which of its sections, as the URL states them. Absent
   * means the screen keeps both in its own state, which is what its unit tests rely on.
   */
  selectedId?: string | null;
  /**
   * `correcting` distinguishes the two ways a selection is cleared. A person going back to the
   * list has navigated, and history should say so; an address naming a business that no longer
   * exists was never a place anybody visited, so it is replaced rather than stacked — otherwise
   * Back would walk them into the dead link they just escaped.
   */
  onSelect?: (tenantId: string | null, options?: { correcting?: boolean }) => void;
  section?: BusinessSection;
  onSectionChange?: (section: BusinessSection) => void;
}

/** Mirrors the server slug rule so a typo is explained before the request. */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

const STATUS_TONE: Record<string, "success" | "warning" | "neutral"> = {
  active: "success",
  suspended: "warning",
  archived: "neutral",
};

function StatusChip({ row }: { row: TenantRow }) {
  const t = useT();
  return (
    <>
      <StatusPill tone={STATUS_TONE[row.status] ?? "neutral"} label={t(`businesses.status.${row.status}`, undefined, row.status)} />
      {!row.sendEnabled && (
        <> <StatusPill tone="warning" label={t("businesses.sendPaused")} /></>
      )}
    </>
  );
}

export function TenantsScreen({
  pushToast,
  tenantContext,
  onBrowse,
  selectedId: routedSelectedId,
  onSelect,
  section,
  onSectionChange,
}: TenantsScreenProps) {
  const t = useT();
  // #1536: the businesses from the last visit stay on screen while they refresh.
  const [rows, setRows] = React.useState<TenantRow[]>(() => cachedRead("listTenants") ?? []);
  const [loading, setLoading] = React.useState(() => cachedRead("listTenants") === undefined);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedId, setSelectedId] = useRoutedState<string | null>(routedSelectedId, onSelect, null);
  const correctSelection = React.useCallback(() => {
    if (onSelect) onSelect(null, { correcting: true });
    else setSelectedId(null);
  }, [onSelect, setSelectedId]);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [showDefaults, setShowDefaults] = React.useState(false);
  /**
   * #1229: the address a business was just given, shown once with what to do with it. Held here
   * rather than in the creation dialog because that dialog closes on success — and this is the
   * one moment the address has somebody's full attention.
   */
  const [newAddress, setNewAddress] = React.useState<string | null>(null);

  // Live refetches can overlap; only the newest response may paint.
  const sequence = React.useRef(0);
  const load = React.useCallback(() => {
    setError(null);
    const mine = ++sequence.current;
    return cockpit.listTenants()
      .then((next) => { if (mine === sequence.current) setRows(next); })
      .catch((e) => { if (mine === sequence.current) setError(e?.message || t("common.error")); })
      .finally(() => { if (mine === sequence.current) setLoading(false); });
  }, [t]);
  React.useEffect(() => { load(); }, [load]);

  // Businesses, their features, owner memberships and the send pause all live on the owner
  // plane, which has its own stream (#549).
  const invalidations = useOwnerLiveInvalidation("tenants", "staff", "settings");
  React.useEffect(() => {
    if (invalidations === 0) return;
    load();
  }, [invalidations, load]);

  const selected = selectedId ? rows.find((r) => r.id === selectedId) ?? null : null;

  // A link to a business that is gone — deleted, archived away, or simply mistyped — collapses to
  // the list rather than to an empty frame, and the address is corrected with it. Only once the
  // list has actually arrived: during the first load nothing is found yet, and clearing then would
  // throw away a perfectly good deep link before it had a chance to resolve.
  React.useEffect(() => {
    if (loading || !selectedId) return;
    if (!rows.some((row) => row.id === selectedId)) correctSelection();
  }, [correctSelection, loading, rows, selectedId]);

  if (selected) {
    return (
      <TenantDetail
        tenant={selected}
        browsing={tenantContext === selected.id}
        pushToast={pushToast}
        onBack={() => setSelectedId(null)}
        onBrowse={onBrowse}
        onChanged={() => { void load(); }}
        section={section}
        onSectionChange={onSectionChange}
      />
    );
  }

  return (
    <div className="page page--management page--businesses page--business-list">
      <div className="page-head">
        <div>
          <h1>{t("businesses.title")}</h1>
          <p>{t("businesses.sub")}</p>
        </div>
        <div className="spacer" />
        <div className="management-actions">
          <Button variant="secondary" size="sm" iconLeft={<Icon name="settings" size={14} />} onClick={() => setShowDefaults(true)}>{t("businesses.features.defaultsAction")}</Button>
          <Button variant="primary" iconLeft={<Icon name="plus" size={16} />} onClick={() => setCreateOpen(true)}>{t("businesses.onboard")}</Button>
        </div>
      </div>

      {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}
      {loading && <p className="subtle" aria-busy="true">{t("common.loading")}</p>}

      <div className="bizlist">
        {!loading && rows.map((row) => (
          <button
            type="button"
            className={`bizcard${tenantContext === row.id ? " bizcard--current" : ""}`}
            key={row.id}
            aria-current={tenantContext === row.id ? "true" : undefined}
            onClick={() => setSelectedId(row.id)}
          >
            <span className="bizcard__main">
              <span className="bizcard__titlerow">
                <b>{row.displayName}</b>
                <StatusChip row={row} />
                {tenantContext === row.id && <span className="quiet-flag">{t("businesses.browsing")}</span>}
                {row.isMember && <span className="quiet-flag">{t("businesses.member")}</span>}
              </span>
              <span className="bizcard__metarow">
                <span className="neo-mono">{row.slug}</span>
                <span aria-hidden="true">·</span>
                <span>{t("businesses.colCreated")} {row.createdAt ? fmtDate(row.createdAt) : t("common.notSet")}</span>
              </span>
            </span>
            <span className="bizcard__arrow" aria-hidden="true"><Icon name="chevronRight" size={16} /></span>
          </button>
        ))}
        {!loading && !rows.length && <p className="subtle">{t("businesses.none")}</p>}
      </div>

      <CreateTenantDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); void load(); }}
        onAddressReady={setNewAddress}
        pushToast={pushToast}
      />
      <WorkingAddressDialog
        open={newAddress !== null}
        address={newAddress ?? ""}
        hosted
        onClose={() => setNewAddress(null)}
      />
      <FeatureDefaultsDialog
        open={showDefaults}
        onClose={() => setShowDefaults(false)}
        pushToast={pushToast}
      />
    </div>
  );
}

function CreateTenantDialog({ open, onClose, onCreated, onAddressReady, pushToast }: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** #1229: an address was issued — hand it to the screen, which explains it once. */
  onAddressReady: (address: string) => void;
  pushToast: TenantsScreenProps["pushToast"];
}) {
  const t = useT();
  const [slug, setSlug] = React.useState("");
  const [displayName, setDisplayName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  // #710: the mailbox choice. Bring-your-own is the default so an established caterer, who is
  // the normal case, is never pushed down a path that takes an address away from them.
  const [choice, setChoice] = React.useState<MailboxChoice>("bring_your_own");
  const [localPart, setLocalPart] = React.useState("");
  const [pickerState, setPickerState] = React.useState<PickerState>({ kind: "idle" });
  // #780: prepare the business for somebody else rather than starting to run it yourself.
  const [prepared, setPrepared] = React.useState(false);
  const offer = useNeoMailboxOffer();

  const slugValue = slug.trim();
  const slugInvalid = slugValue !== "" && !SLUG_RE.test(slugValue);
  // A prepared business gets no address here: it is the invited owner's to choose, once, when
  // they accept. Offering it twice would mean deciding for them and then asking anyway.
  const wantsNeoMailbox = !prepared && choice === "neo_hosted" && offer?.offered === true;
  // The name must be known good before the business is created, so nobody fills in the rest of
  // the form and is told "taken" at the end.
  const mailboxReady = !wantsNeoMailbox || pickedAddress(pickerState) !== null;

  const reset = () => {
    setSlug("");
    setDisplayName("");
    setLocalPart("");
    setChoice("bring_your_own");
    setPickerState({ kind: "idle" });
    setPrepared(false);
  };

  const create = () => {
    if (!slugValue || !displayName.trim() || slugInvalid || !mailboxReady) return;
    setBusy(true);
    registerBusiness(cockpit, {
      slug: slugValue,
      displayName,
      choice: wantsNeoMailbox ? "neo_hosted" : "bring_your_own",
      localPart,
      prepared,
    })
      .then((outcome) => {
        if (outcome.kind === "created_without_mailbox") {
          // The business is real and usable; only the mailbox step failed. Said plainly, with
          // where to continue, rather than reported as a success.
          pushToast({
            tone: "warning",
            title: t("neoMailbox.provisionFailed.title"),
            msg: `${t("neoMailbox.provisionFailed.body")} — ${outcome.error}`,
          });
        } else if (outcome.kind === "created_with_mailbox") {
          // No toast here: the instruction dialog names the same address and says what it is
          // for, and a toast under a modal is read by nobody.
          onAddressReady(outcome.address);
        } else if (prepared) {
          // Said explicitly, because "created" would be misleading: nothing works in this
          // business until somebody accepts the link.
          pushToast({
            tone: "success",
            title: t("businesses.created"),
            msg: t("businesses.preparedCreated"),
          });
        } else {
          pushToast({ tone: "success", title: t("businesses.created") });
        }
        reset();
        onCreated();
      })
      .catch((e: any) => pushToast({ tone: "danger", title: t("common.error"), msg: e?.message }))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} tone="navy" icon={<Icon name="building" size={20} />}
      title={t("businesses.create")}
      footer={<div className="management-actions management-actions--end">
        <Button variant="ghost" disabled={busy} onClick={onClose}>{t("common.cancel")}</Button>
        <Button loading={busy} variant="primary" disabled={busy || !slugValue || slugInvalid || !displayName.trim() || !mailboxReady} iconLeft={<Icon name="plus" size={16} />} onClick={create}>
          {t("businesses.createBtn")}
        </Button>
      </div>}>
      <div className="stack-sm" style={{ paddingBottom: "var(--space-4)" }}>
        <FormField label={t("businesses.colName")}>
          <Input value={displayName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDisplayName(e.target.value)} placeholder="ACME Catering GmbH" />
        </FormField>
        <FormField label={t("businesses.colSlug")}>
          <Input value={slug} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSlug(e.target.value)} placeholder="acme-catering" />
        </FormField>
        {slugInvalid && <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>{t("businesses.slugRule")}</p>}

        <label style={{ display: "flex", gap: "var(--space-2)", alignItems: "flex-start", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={prepared}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrepared(e.target.checked)}
            data-testid="tenant-prepared"
          />
          <span>
            {t("businesses.prepared")}
            <br />
            <span className="subtle" style={{ fontSize: "var(--text-sm)" }}>
              {t("businesses.preparedHint")}
            </span>
          </span>
        </label>

        {/* An installation without a mail server has nothing to offer, so the choice is not
            shown at all and registration behaves exactly as it did before. */}
        {offer?.offered && !prepared && (
          <MailboxChoiceStep
            choice={choice}
            onChoice={setChoice}
            localPart={localPart}
            onLocalPart={setLocalPart}
            onPickerState={setPickerState}
            domain={offer.domain}
          />
        )}
      </div>
    </Dialog>
  );
}

/**
 * The two paths, both first-class (#710). Radio inputs rather than clickable cards: they are
 * keyboard-operable and announce themselves as a choice without any help.
 */
function MailboxChoiceStep({
  choice,
  onChoice,
  localPart,
  onLocalPart,
  onPickerState,
  domain,
}: {
  choice: MailboxChoice;
  onChoice: (choice: MailboxChoice) => void;
  localPart: string;
  onLocalPart: (value: string) => void;
  onPickerState: (state: PickerState) => void;
  domain: string | null;
}) {
  const t = useT();
  const option = (value: MailboxChoice, label: string, hint: string) => (
    <label className="rolepick__option" style={{ cursor: "pointer" }}>
      <input
        type="radio"
        name="neo-mailbox-choice"
        value={value}
        checked={choice === value}
        onChange={() => onChoice(value)}
      />
      <span>
        <b>{label}</b>
        <span className="subtle" style={{ display: "block", fontSize: "var(--text-sm)" }}>{hint}</span>
      </span>
    </label>
  );

  return (
    <div className="stack-sm" data-testid="mailbox-choice">
      <div className="neo-eyebrow" style={{ marginTop: "var(--space-2)" }}>
        {t("neoMailbox.choice.title")}
      </div>
      <div className="rolepick">
        {option("bring_your_own", t("neoMailbox.choice.own"), t("neoMailbox.choice.ownHint"))}
        {option("neo_hosted", t("neoMailbox.choice.hosted"), t("neoMailbox.choice.hostedHint"))}
      </div>
      {choice === "neo_hosted" && (
        <NeoMailboxPicker
          localPart={localPart}
          onLocalPartChange={onLocalPart}
          onStateChange={onPickerState}
          domain={domain}
          inputId="register-neo-local-part"
        />
      )}
    </div>
  );
}

// The vocabulary itself lives in the router, which is the only place that turns it into a path.
type BizSection = BusinessSection;

function TenantDetail({ tenant, browsing, pushToast, onBack, onBrowse, onChanged, section: routedSection, onSectionChange }: {
  tenant: TenantRow;
  browsing: boolean;
  pushToast: TenantsScreenProps["pushToast"];
  onBack: () => void;
  onBrowse: (tenantId: string) => void;
  onChanged: () => void;
  section?: BizSection;
  onSectionChange?: (section: BizSection) => void;
}) {
  const t = useT();
  const [section, setSection] = useRoutedState<BizSection>(routedSection, onSectionChange, "general");

  const sections = [
    { id: "general", label: t("businesses.section.general") },
    { id: "features", label: t("businesses.features.title") },
    { id: "mailboxes", label: t("mailbox.title") },
    { id: "catalog", label: t("businesses.catalog.title") },
    { id: "rules", label: t("businesses.rules.title") },
    { id: "access", label: t("businesses.section.access"), danger: true },
  ];
  const titles: Record<BizSection, string> = {
    general: t("businesses.section.general"),
    features: t("businesses.features.title"),
    mailboxes: t("mailbox.title"),
    catalog: t("businesses.catalog.title"),
    rules: t("businesses.rules.title"),
    access: t("businesses.section.access"),
  };

  return (
    <div className="page page--management page--businesses">
      <div className="detailhead">
        <button className="iconbtn" aria-label={t("common.back")} onClick={onBack}><Icon name="arrowLeft" size={16} /></button>
        <div className="detailhead__id">
          <div className="detailhead__row">
            <h1 className="detailhead__title">{tenant.displayName}</h1>
            <StatusChip row={tenant} />
            {browsing && <span className="quiet-flag">{t("businesses.browsing")}</span>}
            {tenant.isMember && <span className="quiet-flag">{t("businesses.member")}</span>}
          </div>
          <div className="detailhead__meta neo-mono">
            {tenant.slug} · {t("businesses.colCreated")} {tenant.createdAt ? fmtDate(tenant.createdAt) : t("common.notSet")}
          </div>
        </div>
        <span className="spacer" />
        {tenant.status !== "archived" && (
          <Button variant="secondary" size="sm" onClick={() => onBrowse(tenant.id)}>{t("businesses.openAs")}</Button>
        )}
      </div>

      <SectionLayout
        active={section}
        title={titles[section]}
        nav={<SectionNav entries={sections} active={section} onSelect={(id) => setSection(id as BizSection)} ariaLabel={t("businesses.sectionsAria")} />}
      >
        {section === "general" && <GeneralSection tenant={tenant} />}
        {section === "features" && <TenantFeaturesEditor tenantId={tenant.id} pushToast={pushToast} />}
        {section === "mailboxes" && <TenantMailboxesEditor tenantId={tenant.id} pushToast={pushToast} />}
        {section === "catalog" && <TenantCatalogEditor tenantId={tenant.id} pushToast={pushToast} />}
        {/* #1618: re-keyed on status so a suspend/resume in Access re-reads whether it is editable. */}
        {section === "rules" && (
          <TenantRuleParametersEditor key={`${tenant.id}:${tenant.status}`} tenantId={tenant.id} pushToast={pushToast} />
        )}
        {section === "access" && (
          <>
            {/* #780: handing the business to the person who will run it is an access question,
                so it sits with the rest of who-can-get-in rather than in a section of its own. */}
            <div className="management-editor reqventos-access-form">
              <InvitationPanel tenantId={tenant.id} pushToast={pushToast} />
            </div>
            <AccessSection tenant={tenant} pushToast={pushToast} onChanged={onChanged} onArchived={onBack} />
          </>
        )}
      </SectionLayout>
    </div>
  );
}

// #1536: remembered answers pass the same shape checks as fresh ones before they are shown.
function rememberedUsage(tenantId: string): TenantUsage | null {
  const usage = cachedRead("tenantUsage", tenantId);
  return usage && typeof usage === "object" && usage.requests ? usage : null;
}

function rememberedReadiness(tenantId: string): OwnerTenantReadiness | null {
  const readiness = cachedRead("tenantReadiness", tenantId);
  return readiness && typeof readiness === "object" && readiness.rule_parameters ? readiness : null;
}

/** General: identity, live usage, readiness — the former Usage/Readiness dialogs, inline. */
function GeneralSection({ tenant }: { tenant: TenantRow }) {
  const t = useT();
  const tRef = React.useRef(t);
  React.useEffect(() => { tRef.current = t; }, [t]);
  const [usage, setUsage] = React.useState<TenantUsage | null>(() => rememberedUsage(tenant.id));
  const [usageError, setUsageError] = React.useState<string | null>(null);
  const [readiness, setReadiness] = React.useState<OwnerTenantReadiness | null>(() => rememberedReadiness(tenant.id));
  const [readinessError, setReadinessError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setUsage(rememberedUsage(tenant.id));
    setUsageError(null);
    setReadiness(rememberedReadiness(tenant.id));
    setReadinessError(null);
    // Defensive: an unexpected payload must degrade to the error line, never crash the page.
    cockpit.tenantUsage(tenant.id)
      .then((u) => (u && typeof u === "object" && u.requests ? setUsage(u) : setUsageError(tRef.current("common.error"))))
      .catch((e) => setUsageError(e?.message || tRef.current("common.error")));
    cockpit.tenantReadiness(tenant.id)
      .then((r) => (r && typeof r === "object" && r.rule_parameters ? setReadiness(r) : setReadinessError(tRef.current("common.error"))))
      .catch((e) => setReadinessError(e?.message || tRef.current("common.error")));
  }, [tenant.id]);

  return (
    <div className="stack-sm">
      <Card>
        <div className="neo-eyebrow" style={{ marginBottom: "var(--space-3)" }}>{t("businesses.section.identity")}</div>
        <div className="stack-xs reqventos-detail-pairs">
          <div className="neo-kv"><span className="neo-kv__k">{t("businesses.colName")}</span><span className="neo-kv__v">{tenant.displayName}</span></div>
          <div className="neo-kv"><span className="neo-kv__k">{t("businesses.colSlug")}</span><span className="neo-kv__v neo-mono">{tenant.slug}</span></div>
          <div className="neo-kv"><span className="neo-kv__k">{t("businesses.colStatus")}</span><span className="neo-kv__v"><StatusChip row={tenant} /></span></div>
          <div className="neo-kv"><span className="neo-kv__k">{t("businesses.colCreated")}</span><span className="neo-kv__v">{tenant.createdAt ? fmtDate(tenant.createdAt) : t("common.notSet")}</span></div>
        </div>
      </Card>

      <Card>
        <div className="neo-eyebrow" style={{ marginBottom: "var(--space-3)" }}>{t("businesses.usageTitle")}</div>
        {usageError && <Banner tone="danger" title={t("common.error")}>{usageError}</Banner>}
        {!usage && !usageError && <p className="subtle" aria-busy="true">{t("common.loading")}</p>}
        {usage && (
          <div className="statgrid">
            <div className="statgrid__cell"><div className="statgrid__n">{usage.requests.total}</div><div className="statgrid__l">{t("businesses.usageRequests")}</div><div className="statgrid__s">{usage.requests.open} {t("businesses.usageOpen")}</div></div>
            <div className="statgrid__cell"><div className="statgrid__n">{usage.messages.total}</div><div className="statgrid__l">{t("businesses.usageMessages")}</div><div className="statgrid__s">{usage.messages.inbound} {t("businesses.usageInbound")}</div></div>
            <div className="statgrid__cell"><div className="statgrid__n">{usage.users.total}</div><div className="statgrid__l">{t("businesses.usageUsers")}</div><div className="statgrid__s" /></div>
            <div className="statgrid__cell"><div className="statgrid__n">{usage.sends.total}</div><div className="statgrid__l">{t("businesses.usageSends")}</div><div className="statgrid__s" /></div>
            <div className="statgrid__cell"><div className="statgrid__n">{usage.llmCalls.total}</div><div className="statgrid__l">{t("businesses.usageLlm")}</div><div className="statgrid__s">{usage.llmCalls.last30d} {t("businesses.usageLast30d")}</div></div>
          </div>
        )}
      </Card>

      <Card>
        <div className="neo-eyebrow" style={{ marginBottom: "var(--space-3)" }}>{t("businesses.readiness.title")}</div>
        {readinessError && <Banner tone="danger" title={t("common.error")}>{readinessError}</Banner>}
        {!readiness && !readinessError && <p className="subtle" aria-busy="true">{t("common.loading")}</p>}
        {readiness && (
          <div className="stack-xs reqventos-detail-pairs">
            <div className="neo-kv">
              <span className="neo-kv__k">{t("businesses.readiness.overall")}</span>
              <span className="neo-kv__v">
                <StatusPill
                  tone={readiness.ready ? "success" : "warning"}
                  label={t(readiness.ready ? "businesses.readiness.ready" : "businesses.readiness.notReady")}
                />
              </span>
            </div>
            <div className="neo-kv">
              <span className="neo-kv__k">{t("businesses.readiness.parameters")}</span>
              <span className="neo-kv__v">
                {t(`businesses.readiness.status.${readiness.rule_parameters.status}`, undefined, readiness.rule_parameters.status)}
                {readiness.rule_parameters.error_code && (
                  <> · <code>{readiness.rule_parameters.error_code}</code></>
                )}
              </span>
            </div>
            <div className="neo-kv">
              <span className="neo-kv__k">{t("businesses.readiness.policy")}</span>
              <span className="neo-kv__v"><code>{readiness.rules_authority}</code></span>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

/** Access: send pause, suspend/resume, archive — the prototype's danger section. */
function AccessSection({ tenant, pushToast, onChanged, onArchived }: {
  tenant: TenantRow;
  pushToast: TenantsScreenProps["pushToast"];
  onChanged: () => void;
  onArchived: () => void;
}) {
  const t = useT();
  const [busy, setBusy] = React.useState(false);
  const [confirmArchive, setConfirmArchive] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const run = (p: Promise<unknown>, ok: string, after?: () => void) => {
    setBusy(true);
    setError(null);
    p.then(() => {
      pushToast({ tone: "success", title: ok });
      (after ?? onChanged)();
    })
      .catch((e: any) => setError(e?.message || t("common.error")))
      .finally(() => setBusy(false));
  };

  const archived = tenant.status === "archived";

  return (
    <div className="stack-sm">
      {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}

      <div className="accessrow">
        <div className="accessrow__text">
          <div className="accessrow__title">
            {t(tenant.isMember ? "businesses.membership.leaveTitle" : "businesses.membership.joinTitle")}
          </div>
          <div className="accessrow__sub">{t("businesses.membership.sub")}</div>
        </div>
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => run(
            cockpit.setOwnerMembership(tenant.id, !tenant.isMember),
            t(tenant.isMember ? "businesses.membership.left" : "businesses.membership.joined"),
          )}
        >
          {t(tenant.isMember ? "businesses.membership.leave" : "businesses.membership.join")}
        </Button>
      </div>

      {!archived && (
        <div className="accessrow">
          <div className="accessrow__text">
            <div className="accessrow__title">{t(tenant.sendEnabled ? "businesses.pauseSending" : "businesses.resumeSending")}</div>
            <div className="accessrow__sub">{t("businesses.pauseSub")}</div>
          </div>
          <Button variant="secondary" disabled={busy}
            onClick={() => run(
              cockpit.setTenantSendEnabled(tenant.id, !tenant.sendEnabled),
              t(tenant.sendEnabled ? "businesses.sendPausedToast" : "businesses.sendResumedToast"),
            )}>
            {t(tenant.sendEnabled ? "businesses.sendPause" : "businesses.sendResume")}
          </Button>
        </div>
      )}

      {tenant.status === "active" && (
        <div className="accessrow">
          <div className="accessrow__text">
            <div className="accessrow__title">{t("businesses.suspend")}</div>
            <div className="accessrow__sub">{t("businesses.suspendSub")}</div>
          </div>
          <Button variant="secondary" disabled={busy}
            onClick={() => run(cockpit.suspendTenant(tenant.id), t("businesses.suspended"))}>
            {t("businesses.suspend")}
          </Button>
        </div>
      )}

      {tenant.status === "suspended" && (
        <div className="accessrow">
          <div className="accessrow__text">
            <div className="accessrow__title">{t("businesses.resume")}</div>
            <div className="accessrow__sub">{t("businesses.resumeSub")}</div>
          </div>
          <Button variant="secondary" disabled={busy}
            onClick={() => run(cockpit.resumeTenant(tenant.id), t("businesses.resumed"))}>
            {t("businesses.resume")}
          </Button>
        </div>
      )}

      {!archived && (
        <div className="dangercard">
          <div className="dangercard__title">{t("businesses.archiveTitle")}</div>
          <p style={{ margin: "0 0 6px" }}>{t("businesses.archiveConfirm")}</p>
          <p style={{ margin: "0 0 14px", fontWeight: "var(--weight-semibold)" }}>
            {tenant.displayName} (<span className="neo-mono">{tenant.slug}</span>)
          </p>
          <p className="subtle" style={{ margin: "0 0 20px", fontSize: "var(--text-sm)" }}>{t("businesses.archiveIrreversible")}</p>
          {!confirmArchive ? (
            <Button variant="danger-quiet" onClick={() => setConfirmArchive(true)}>{t("businesses.archive")}</Button>
          ) : (
            <div className="management-actions">
              <Button variant="ghost" disabled={busy} onClick={() => setConfirmArchive(false)}>{t("common.cancel")}</Button>
              <Button loading={busy} variant="destructive" disabled={busy}
                onClick={() => run(cockpit.archiveTenant(tenant.id), t("businesses.archived"), () => { onChanged(); onArchived(); })}>
                {t("businesses.archiveFinal")}
              </Button>
            </div>
          )}
        </div>
      )}

      {archived && <p className="subtle">{t("businesses.archivedNote")}</p>}
    </div>
  );
}
